import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type {
  AgentScheduleResponse,
  ExpectationTrend,
  ExpectedContactRate,
  ModelHistoryResponse,
  ModelStatusResponse,
  PlanCapacity,
  PlanRecommendedCoverage,
  PlanRosterOptimizedResponse,
  PulseSummary,
  QueueResponse,
  ScorecardResponse,
  ScorecardCohortResponse,
  ScorecardCohortLeadsResponse,
  CohortGrain,
  TimingAdjusted,
  TimingHeatmap,
  TimingRecommendedWindows,
} from '../types/contactOptimization';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json();
}

/** Every section endpoint below is Postgres-cached server-side (several
 * hours' TTL -- see cache.py); `{ refresh: true }` is the one, consistent
 * way any caller asks for a live recompute instead of that cached
 * payload -- used by the tab bar's Refresh button, not by ordinary
 * page-load fetches. */
export interface FetchOpts {
  refresh?: boolean;
}

function withRefreshParam(params: URLSearchParams, opts?: FetchOpts): URLSearchParams {
  if (opts?.refresh) params.set('refresh', 'true');
  return params;
}

export async function fetchPulseSummary(opts?: FetchOpts): Promise<PulseSummary> {
  const qs = withRefreshParam(new URLSearchParams(), opts).toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/pulse${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<PulseSummary>(response, 'Failed to load Pulse summary');
}

export async function fetchTimingHeatmap(states?: string[], opts?: FetchOpts): Promise<TimingHeatmap> {
  const params = new URLSearchParams();
  for (const st of states ?? []) params.append('state', st);
  withRefreshParam(params, opts);
  const qs = params.toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/timing${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<TimingHeatmap>(response, 'Failed to load Timing heatmap');
}

export async function fetchTimingAdjusted(states: string[], opts?: FetchOpts): Promise<TimingAdjusted> {
  const params = new URLSearchParams();
  for (const st of states) params.append('state', st);
  withRefreshParam(params, opts);
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/timing/adjusted?${params.toString()}`),
    { headers: getAuthHeaders() },
  );
  return readJson<TimingAdjusted>(response, 'Failed to load model-adjusted view');
}

export async function fetchTimingRecommendedWindows(opts?: FetchOpts): Promise<TimingRecommendedWindows> {
  const qs = withRefreshParam(new URLSearchParams(), opts).toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/timing/recommended-windows${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<TimingRecommendedWindows>(response, 'Failed to load recommended calling windows');
}

export async function downloadTimingRecommendedWindowsXlsx(): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/timing/recommended-windows/export.xlsx`),
    { headers: getAuthHeaders() },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Failed to download workbook');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'recommended-calling-windows.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchPlanCapacity(opts?: FetchOpts): Promise<PlanCapacity> {
  const qs = withRefreshParam(new URLSearchParams(), opts).toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/plan${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<PlanCapacity>(response, 'Failed to load Plan capacity');
}

export async function fetchPlanRecommendedCoverage(opts?: FetchOpts): Promise<PlanRecommendedCoverage> {
  const qs = withRefreshParam(new URLSearchParams(), opts).toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/plan/recommended-coverage${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<PlanRecommendedCoverage>(response, 'Failed to load recommended-hours coverage');
}

export async function fetchAgentSchedule(opts?: FetchOpts): Promise<AgentScheduleResponse> {
  const qs = withRefreshParam(new URLSearchParams(), opts).toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/plan/schedule${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<AgentScheduleResponse>(response, 'Failed to load agent schedule');
}

export async function fetchPlanRosterOptimized(
  opts?: FetchOpts & { hours?: number },
): Promise<PlanRosterOptimizedResponse> {
  const params = new URLSearchParams();
  if (opts?.hours != null) params.set('hours', String(opts.hours));
  const qs = withRefreshParam(params, opts).toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/plan/roster-optimized${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<PlanRosterOptimizedResponse>(response, 'Failed to load the optimized roster');
}

export async function downloadPlanRosterOptimizedXlsx(hours?: number): Promise<void> {
  const params = new URLSearchParams();
  if (hours != null) params.set('hours', String(hours));
  const qs = params.toString();
  const response = await fetch(
    buildApiUrl(
      `${requireTenantPrefix()}/contact-optimization/plan/roster-optimized/export.xlsx${qs ? `?${qs}` : ''}`,
    ),
    { headers: getAuthHeaders() },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Failed to download roster workbook');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = hours != null ? `optimized-roster-${hours}h.xlsx` : 'optimized-roster.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchExpectedContactRate(opts?: FetchOpts): Promise<ExpectedContactRate> {
  const qs = withRefreshParam(new URLSearchParams(), opts).toString();
  const response = await fetch(
    buildApiUrl(
      `${requireTenantPrefix()}/contact-optimization/scorecard/expected-contact-rate${qs ? `?${qs}` : ''}`,
    ),
    { headers: getAuthHeaders() },
  );
  return readJson<ExpectedContactRate>(response, 'Failed to load expected contact rate');
}

export async function fetchExpectationTrend(): Promise<ExpectationTrend> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/scorecard/expectation-trend`),
    { headers: getAuthHeaders() },
  );
  return readJson<ExpectationTrend>(response, 'Failed to load the expected connect rate trend');
}

export async function fetchQueue(opts?: FetchOpts): Promise<QueueResponse> {
  const qs = withRefreshParam(new URLSearchParams(), opts).toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/queue${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<QueueResponse>(response, 'Failed to load queue');
}

/** The working file the customer acts on: bands, reasons, and a sheet shaped
 * for upload into Regal. Server-built rather than exported from the grid,
 * because the grid only holds what is currently filtered and on screen. */
export async function downloadQueueXlsx(): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/queue/export.xlsx`),
    { headers: getAuthHeaders() },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Failed to download workbook');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Dated, because this file is only right for the hour it was built.
  a.download = `calling-queue-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchModelStatus(): Promise<ModelStatusResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/model/status`),
    { headers: getAuthHeaders() },
  );
  return readJson<ModelStatusResponse>(response, 'Failed to load model status');
}

export async function fetchModelHistory(): Promise<ModelHistoryResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/model/history`),
    { headers: getAuthHeaders() },
  );
  return readJson<ModelHistoryResponse>(response, 'Failed to load model history');
}

export async function triggerModelTraining(): Promise<ModelStatusResponse['training']> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/model/train`),
    { method: 'POST', headers: getAuthHeaders() },
  );
  return readJson(response, 'Failed to start training');
}

/** the customer's own 24 KPIs, team and per agent.
 *
 * Dates are optional: omitted, the backend defaults to week-to-date
 * (Monday through yesterday) and serves that from its hourly cache. An
 * explicit range is computed live -- caching every range someone might
 * pick would fill the cache table with rows nobody asks for twice. */
export async function fetchScorecard(
  range?: { startDate?: string; endDate?: string },
  opts?: FetchOpts,
): Promise<ScorecardResponse> {
  const params = new URLSearchParams();
  if (range?.startDate) params.set('start_date', range.startDate);
  if (range?.endDate) params.set('end_date', range.endDate);
  withRefreshParam(params, opts);
  const qs = params.toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/scorecard${qs ? `?${qs}` : ''}`),
    { headers: getAuthHeaders() },
  );
  return readJson<ScorecardResponse>(response, 'Failed to load the Scorecard');
}

/** The Scorecard's cohort grid: leads grouped by the day they arrived,
 * against how many days later we first got them on the phone.
 *
 * Only the default window (last 7 days) is cached server-side. Every other
 * range computes live, since caching hand-picked ranges would fill the cache
 * table with rows nobody asks for twice. */
export async function fetchScorecardCohort(
  range: { startDate: string; endDate: string; grain?: CohortGrain },
  opts?: FetchOpts,
): Promise<ScorecardCohortResponse> {
  const params = new URLSearchParams();
  params.set('start_date', range.startDate);
  params.set('end_date', range.endDate);
  if (range.grain) params.set('grain', range.grain);
  // Only the default window is cached server-side, so refresh is a no-op on
  // any other range -- harmless, and it keeps one code path for both.
  withRefreshParam(params, opts);
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/scorecard/cohort?${params}`),
    { headers: getAuthHeaders() },
  );
  return readJson<ScorecardCohortResponse>(response, 'Failed to load the cohort table');
}

/** The leads behind one cohort row. Lazy: only fetched when a row is opened,
 * and paginated, because a monthly bucket can hold thousands. */
export async function fetchScorecardCohortLeads(args: {
  bucketStart: string;
  bucketEnd: string;
  outcome?: string;
  limit?: number;
  offset?: number;
}): Promise<ScorecardCohortLeadsResponse> {
  const params = new URLSearchParams();
  params.set('bucket_start', args.bucketStart);
  params.set('bucket_end', args.bucketEnd);
  if (args.outcome && args.outcome !== 'all') params.set('outcome', args.outcome);
  params.set('limit', String(args.limit ?? 50));
  params.set('offset', String(args.offset ?? 0));
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/contact-optimization/scorecard/cohort/leads?${params}`),
    { headers: getAuthHeaders() },
  );
  return readJson<ScorecardCohortLeadsResponse>(response, 'Failed to load leads for this row');
}
